import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"

export const PAGE_QUERY = "page"
export const PAGE_SIZE_QUERY = "pageSize"

type PaginationState = {
    pageIndex: number
    pageSize: number
}

type Updater<T> = T | ((prev: T) => T)

export function usePagination(
    manageViaUrl: boolean,
    defaultPageIndex: number = 0,
    defaultPageSize: number = 10,
): [PaginationState, (updater: Updater<PaginationState>) => void] {
    const [searchParams, setSearchParams] = useSearchParams()

    // Initialize pagination state from URL or with defaults
    const [pagination, setPagination] = useState(() => {
        const pageIndex = manageViaUrl
            ? parseInt(searchParams.get(PAGE_QUERY) || `${defaultPageIndex}`, 10)
            : defaultPageIndex
        const pageSize = manageViaUrl
            ? parseInt(searchParams.get(PAGE_SIZE_QUERY) || `${defaultPageSize}`, 10)
            : defaultPageSize
        return { pageIndex, pageSize }
    })

    // Function to update pagination state and URL if managed via URL
    const updatePagination = (updater: Updater<PaginationState>) => {
        setPagination((prev) => {
            const newPagination = typeof updater === "function" ? updater(prev) : updater
            if (manageViaUrl) {
                searchParams.set(PAGE_QUERY, newPagination.pageIndex.toString())
                searchParams.set(PAGE_SIZE_QUERY, newPagination.pageSize.toString())
                setSearchParams(searchParams, { replace: true })
            }
            return newPagination
        })
    }

    // Ensure pagination state is in sync with URL changes
    useEffect(() => {
        if (manageViaUrl) {
            const newPageIndex = parseInt(searchParams.get(PAGE_QUERY) || `${defaultPageIndex}`, 10)
            const newPageSize = parseInt(searchParams.get(PAGE_SIZE_QUERY) || `${defaultPageSize}`, 10)
            if (newPageIndex !== pagination.pageIndex || newPageSize !== pagination.pageSize) {
                setPagination({ pageIndex: newPageIndex, pageSize: newPageSize })
            }
        }
    }, [searchParams, manageViaUrl, defaultPageIndex, defaultPageSize])

    return [pagination, updatePagination]
}
